import { useState } from 'react'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import TaActionButton from '../TaActionButton'
import { useAppActions } from '../../AppContext'
import { useQuestionProcessing } from '../../services/TaskManagerService'
import BidBatchProcessModal from '../../tabs/modals/BidBatchProcessModal'
import { ICON_SM } from '../../common/theme'
import type { Question } from '../../types'
import { executeGenerateBatch } from '../../services/QuestionActions'

interface GenerateResponseButtonProps {
  question: Question,
  mode?: 'modal' | 'immediate'
}

// Toolbar button that fires the per-question answer-generation job.
// Owns the click → batch-process → result flow internally so callers
// just drop it next to a question. Disables/spins from the same signals
// every other generator-button surface uses:
//   • question.processing.answer - set by startGenerateQuestions on
//     dispatch, cleared by the registry when the run finishes.
//   • the taskstream's queued / running flags on this question's
//     streaming entry, so the button still reflects the live job state
//     across page reloads.
export default function GenerateResponseButton({ question, mode = 'modal' }: GenerateResponseButtonProps) {
  const { updateQuestion, getCurrentProject, getCurrentStyleName } = useAppActions()
  const processing = useQuestionProcessing(question.questionsha)
  // Modal payload - populated on click via the ref-backed getters so the
  // button itself never subscribes to currentProject / currentStyleName,
  // and therefore doesn't re-render on every keystroke.
  const [modalSnap, setModalSnap] = useState<{
    projectsha: string
    projectbideval: any
    styleName: string
  } | null>(null)

  const queued = processing.isqueuedanswer
  const running = processing.isrunninganswer

  const handleClick = async () => {
    const project = getCurrentProject()
    if (!project) return
    const styleName = getCurrentStyleName()
    if (mode === 'modal') {
      setModalSnap({
        projectsha: project.projectsha,
        projectbideval: (project as any).projectbideval,
        styleName,
      })
      return
    }
    // Immediate path - fire the batch with defaults. The modal also goes
    // through executeGenerateBatch; this just skips the option picker.
    await executeGenerateBatch(
      [question],
      project,
      styleName,
      updateQuestion,
    )
  }

  const title = queued
    ? 'Queued'
    : running
      ? 'Generating…'
      : 'Generate Answer'

  return (
    <>
      <TaActionButton
        title={title}
        onClick={handleClick}
        running={running}
        queued={queued}
        icon={<AutorenewIcon sx={{ fontSize: ICON_SM }} />}
      />
      {mode === 'modal' && modalSnap && (
        <BidBatchProcessModal
          open={true}
          projectsha={modalSnap.projectsha}
          questions={[question]}
          projectbideval={modalSnap.projectbideval}
          defaultStyleName={modalSnap.styleName}
          onClose={() => setModalSnap(null)}
        />
      )}
    </>
  )
}
